import React from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { colorForRate, formatEuro, toNumber } from './evaluationFormatters';
import { buildVatFallbackHint } from './evaluationMessages';

export default function MonthlySection({ data }) {
    const rows = React.useMemo(() => {
        if (!data?.sales) return [];
        return Object.entries(data.sales).map(([rateLabel, netAny]) => {
            const net = toNumber(netAny);
            const rate = Number(String(rateLabel).replace('%', '')) || 0;
            const tax = net * (rate / 100);
            const gross = net + tax;
            return { rateLabel, net, tax, gross };
        });
    }, [data]);

    const totals = React.useMemo(() => {
        const sumNet = rows.reduce((a, r) => a + r.net, 0);
        const sumTax = rows.reduce((a, r) => a + r.tax, 0);
        const sumGross = rows.reduce((a, r) => a + r.gross, 0);
        const inputTax = toNumber(data?.inputTax);
        const euTaxableAmount = toNumber(data?.euTaxableAmount);
        const euVat = toNumber(data?.euVat);
        const euInputTax = toNumber(data?.euInputTax);
        const fallbackCount = Number(data?.inputTaxFallbackCount) || 0;
        const fallbackAmount = toNumber(data?.inputTaxFallbackAmount);
        const fallbackHint = buildVatFallbackHint(fallbackCount, fallbackAmount);
        const payable = sumTax + euVat - inputTax - euInputTax;
        return { sumNet, sumTax, sumGross, inputTax, euTaxableAmount, euVat, euInputTax, payable, fallbackCount, fallbackAmount, fallbackHint };
    }, [rows, data]);

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    mb: 2,
                    borderColor: totals.payable >= 0 ? 'divider' : 'success.light',
                    background: totals.payable >= 0 ? 'transparent' : 'rgba(46, 160, 67, 0.06)',
                }}
            >
                <CardContent sx={{ py: 2.5 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 220, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Nettoumsatz
                            </Typography>
                            <Typography variant="h6">{formatEuro(totals.sumNet)}</Typography>
                        </Box>
                        <Box sx={{ minWidth: 220, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Umsatzsteuer
                            </Typography>
                            <Typography variant="h6">{formatEuro(totals.sumTax)}</Typography>
                        </Box>
                        <Box sx={{ minWidth: 220, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Vorsteuer
                            </Typography>
                            <Typography variant="h6">{formatEuro(totals.inputTax)}</Typography>
                        </Box>
                        <Box sx={{ minWidth: 260, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Zahllast / Erstattung
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                                <Typography variant="h6">{formatEuro(totals.payable)}</Typography>
                                <Chip
                                    label={totals.payable >= 0 ? 'Zahllast' : 'Erstattung'}
                                    color={totals.payable >= 0 ? 'warning' : 'success'}
                                    size="small"
                                    variant="outlined"
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {totals.fallbackHint && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {totals.fallbackHint}
                </Alert>
            )}

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, mb: 3 }}>
                <Table aria-label="Tabelle Umsatzsteuer je Steuersatz">
                    <TableHead>
                        <TableRow>
                            <TableCell>Steuersatz</TableCell>
                            <TableCell align="right">Netto</TableCell>
                            <TableCell align="right">USt</TableCell>
                            <TableCell align="right">Brutto</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.rateLabel} sx={{ backgroundColor: colorForRate(row.rateLabel) }}>
                                <TableCell>{row.rateLabel}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.net)}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.tax)}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.gross)}
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Summe</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(rows.reduce((a, r) => a + r.net, 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(rows.reduce((a, r) => a + r.tax, 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(rows.reduce((a, r) => a + r.gross, 0))}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" sx={{ mb: 1, mt: 3 }}>
                EU-Ausgaben (Reverse Charge)
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
                <Table aria-label="Tabelle EU-Ausgaben">
                    <TableHead>
                        <TableRow>
                            <TableCell>Beschreibung</TableCell>
                            <TableCell align="right">Betrag</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Bemessungsgrundlage</TableCell>
                            <TableCell align="right">{formatEuro(totals.euTaxableAmount)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Umsatzsteuer</TableCell>
                            <TableCell align="right">{formatEuro(totals.euVat)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Vorsteuer</TableCell>
                            <TableCell align="right">{formatEuro(totals.euInputTax)}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
